import moment, { Moment } from "moment";

export type SettlementPeriodType = "d" | "w" | "m" | "r";
export type SettlementPeriodDirection = -1 | 1;

export interface SettlementDateRange {
  start: string;
  end: string;
}

function formatDate(date: Moment): string {
  return date.format("YYYY-MM-DD");
}

function startOfDay(date: Moment): Moment {
  return date.clone().startOf("day");
}

/**
 * Classifies a settlement range using the same date semantics as the UI.
 * A current, incomplete week or month is recognized through today's date.
 */
export function getSettlementPeriodType(
  startDate: string,
  endDate: string,
  today: Moment = moment()
): SettlementPeriodType {
  const start = moment(startDate);
  const end = moment(endDate);

  if (!start.isValid() || !end.isValid()) return "r";

  const currentDay = startOfDay(today);
  if (start.isSame(end, "day")) return "d";

  const isSunday = start.day() === 0;
  const saturdayOfWeek = moment(start).day(6);
  const cappedSaturday = moment.min(saturdayOfWeek, currentDay);
  if (isSunday && end.isSame(cappedSaturday, "day")) return "w";

  const isFirstDay = start.date() === 1;
  const lastDayOfMonth = moment(start).endOf("month");
  const cappedMonthEnd = moment.min(lastDayOfMonth, currentDay);
  if (isFirstDay && end.isSame(cappedMonthEnd, "day")) return "m";

  return "r";
}

/**
 * Returns the adjacent range for a settlement period.
 * A null result means that the target range would be invalid or in the future.
 */
export function getAdjacentSettlementRange(
  startDate: string,
  endDate: string,
  direction: SettlementPeriodDirection,
  today: Moment = moment()
): SettlementDateRange | null {
  const start = startOfDay(moment(startDate));
  const end = startOfDay(moment(endDate));
  const currentDay = startOfDay(today);

  if (!start.isValid() || !end.isValid() || end.isBefore(start, "day")) {
    return null;
  }

  const periodType = getSettlementPeriodType(startDate, endDate, currentDay);
  let nextStart: Moment;
  let nextEnd: Moment;

  if (periodType === "d") {
    nextStart = start.clone().add(direction, "day");
    nextEnd = nextStart.clone();
  } else if (periodType === "w") {
    nextStart = start.clone().add(direction * 7, "days");
    nextEnd = nextStart.clone().add(6, "days");
    nextEnd = moment.min(nextEnd, currentDay);
  } else if (periodType === "m") {
    nextStart = start.clone().startOf("month").add(direction, "month");
    nextEnd = nextStart.clone().endOf("month");
    nextEnd = moment.min(nextEnd, currentDay);
  } else {
    const dayCount = end.diff(start, "days") + 1;
    nextStart = start.clone().add(direction * dayCount, "days");
    nextEnd = end.clone().add(direction * dayCount, "days");
  }

  if (
    nextStart.isAfter(currentDay, "day") ||
    (direction === 1 && nextEnd.isAfter(currentDay, "day"))
  ) {
    return null;
  }

  return {
    start: formatDate(nextStart),
    end: formatDate(nextEnd),
  };
}
